import { Buffer } from 'buffer';
import mem from 'mem';
import { debounce } from 'lodash';

import { graphQLClient } from '@api/client';
import { RefreshTokenObject } from '@api/GoodocAPI';
import {
  getAccessToken,
  getRefreshToken,
  getRefreshTokenExpiredAt,
  removeAccessToken,
  removeRefreshToken,
  removeRefreshTokenExpiredAt,
  saveAccessToken,
  saveRefreshToken,
  saveRefreshTokenExpiredAt
} from './localStorage';

interface TokenDataType {
  accessToken: string;
  refreshToken: string;
  refreshTokenExpiredAt: any;
}

export const checkAccessTokenInvalid = () => {
  const accessToken = getAccessToken();
  return accessToken ? checkAccessTokenExpired(accessToken) : true;
};

export const checkAccessTokenExpired = (accessToken: string) => {
  const splittedToken = accessToken.split('.');
  const { exp } = JSON.parse(
    Buffer.from(splittedToken[1] || '', 'base64').toString('utf8')
  );
  return Date.now() >= exp * 1000;
};

export const checkRefreshTokenExpired = () => {
  const refreshToken = getRefreshToken();
  const refreshTokenExpiredAt = getRefreshTokenExpiredAt();
  return !refreshToken
    ? true
    : Date.now() >= new Date(refreshTokenExpiredAt || '').getTime();
};

export const getAccessTokenForAuthorizatonHeader = () => {
  const accessToken = getAccessToken();
  return accessToken ? `Bearer ${accessToken}` : '';
};

export const rotateTokenViaRefreshToken = async () => {
  const { query, variables, url } = getRotateTokenParameters();
  const fetchResult = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      query,
      variables
    })
  });
  return await handleRotateTokenResult(await fetchResult.json());
};

const handleRotateTokenResult = async ({
  data,
  errors
}: {
  data: { rotateRefreshToken: RefreshTokenObject };
  errors: any;
}) => {
  if (errors) {
    const { exception } = errors[0].extensions;
    console.error('error while rotate refresh token', JSON.stringify(exception, null, 2));
    debounceMoveToSignIn();
    return;
  }
  const { rotateRefreshToken } = data;
  updateAuthData(rotateRefreshToken);
  return rotateRefreshToken;
};

export const memoizedRotateTokenViaRefreshToken = mem(
  async (refreshToken: string | null) => {
    console.debug(
      '[memoizedRotateAccessTokenViaRefreshToken] refreshToken:',
      refreshToken
    );
    return await rotateTokenViaRefreshToken();
  }
);

export const updateAuthData = (tokenData: TokenDataType) => {
  graphQLClient.setHeader('Authorization', `Bearer ${tokenData.accessToken}`);
  updateTokenInStorage(tokenData);
};

export const updateTokenInStorage = (tokenData: TokenDataType) => {
  const { accessToken, refreshToken, refreshTokenExpiredAt } = tokenData;

  saveAccessToken(accessToken);
  saveRefreshToken(refreshToken);
  saveRefreshTokenExpiredAt(refreshTokenExpiredAt);
};

const getRotateTokenParameters = () => {
  const query = `mutation RotateRefreshToken($refreshToken: String!) {
      rotateRefreshToken(refreshToken: $refreshToken) {
        accessToken
        refreshToken
        refreshTokenExpiredAt
      }
    }
  `;
  const variables = {
    refreshToken: getRefreshToken() || ''
  };
  const url = process.env.REACT_APP_GOODOC_API_URL || '';
  return { query, variables, url };
};

export const removeTokenInStorage = () => {
  removeAccessToken();
  removeRefreshToken();
  removeRefreshTokenExpiredAt();
  graphQLClient.setHeader('Authorization', '');
};

const moveToSignIn = () => {
  removeTokenInStorage();
  const currentPath = window.location.pathname;
  if (!currentPath.includes('signIn') && currentPath !== '/') {
    window.location.href = `/signIn?from=${currentPath.replaceAll('/', '%2F')}`;
  }
};

export const debounceMoveToSignIn = debounce(moveToSignIn, 1000);

export const needSignIn = () => {
  const currentPath = window.location.pathname;
  return (
    !currentPath.includes('signIn') &&
    currentPath !== '/' &&
    !currentPath.includes('department')
  );
};
